<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { errorDialogReloadClicked, errorDialogStatusLinkClicked } from "@/analytics/events/error";
import { trackEvent } from "@/analytics/track";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import CopyButton from "@/components/ui/CopyButton/CopyButton.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { ApplicationError } from "@/error/ApplicationError";
import { reload } from "@/error/reload";
import { currentRoute } from "@/router/navigation";
import { useGlobalErrorStore } from "@/store/globalError";
import { useUserStore } from "@/store/user";
import { isCloud } from "@/utils/env/Environment";

const { t } = useI18n();
const detailsContentRef = ref<HTMLElement>();

function reloadPage() {
  trackEvent(errorDialogReloadClicked());
  reload("manual-global-error");
}

function trackStatusLinkClicked() {
  trackEvent(errorDialogStatusLinkClicked());
}

function detailsText() {
  return detailsContentRef.value?.innerText?.trim() || "";
}

const error = computed(() => useGlobalErrorStore().globalError);
const errorDetails = computed(() => useGlobalErrorStore().errorDetails);
const errorDetailsURI = computed(() =>
  encodeURIComponent(JSON.stringify(errorDetails.value || {})),
);
const location = computed(() => window.location.href);
const message = computed(() => {
  const err = error.value?.error;
  if (err instanceof ApplicationError) {
    return JSON.stringify(err.contexts, null, 2);
  }
  return err instanceof Error ? err.stack : error.value?.message;
});

const followupActions = computed(() =>
  isCloud && useUserStore().isAllowed("contactSupport")
    ? t("globalError.contactSupport")
    : t("globalError.informAdmin"),
);
</script>

<template>
  <div
    v-if="error"
    v-focus-trap
    class="global-error"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="global-error-title"
  >
    <div class="card">
      <div class="main">
        <div class="logo">
          <SvgIcon name="general/logo-named" aria-hidden="true" height="24" />
        </div>
        <div class="text">
          <h1 id="global-error-title" class="title">{{ $t("globalError.somethingWrong") }}</h1>
          <p class="description">
            <I18nT :keypath="/*$t*/ 'globalError.tryCheckStatus'" scope="global">
              <template #status>
                <a
                  :href="`/#/status?errorDetails=${errorDetailsURI}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="trackStatusLinkClicked"
                >
                  {{ $t("globalError.statusPage") }}
                </a>
              </template>
            </I18nT>
            {{ followupActions }}
          </p>
        </div>
        <BaseButton
          variant="outlined"
          color="grey"
          :full-width="true"
          icon-before="system/refresh"
          @click="reloadPage"
        >
          {{ $t("globalError.reloadPage") }}
        </BaseButton>
      </div>
      <div class="details">
        <p id="global-error-details-label" class="details-label">
          {{ $t("globalError.technicalDetails") }}
        </p>
        <div
          class="details-content scrollable scrollable-relative"
          role="region"
          aria-labelledby="global-error-details-label"
        >
          <CopyButton :text="detailsText" class="copy-button" />
          <div ref="detailsContentRef">
            <div v-if="errorDetails?.time">{{ errorDetails.time }}</div>
            <div>{{ location }}</div>
            <div v-if="currentRoute()?.name">{{ currentRoute()?.name }}</div>
            <div v-if="errorDetails?.shortMessage">{{ errorDetails.shortMessage }}</div>
            <div v-if="errorDetails?.correlationId">
              {{ $t("globalError.correlationId", { id: errorDetails.correlationId }) }}
            </div>
            <div v-if="message">{{ message }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";
@use "@/styles/z-index";
@use "@/styles/mixins/typography";

.global-error {
  position: fixed;
  inset: 0;
  background-color: colors.$background-grey-light;
  z-index: z-index.$modal-overlay;
  display: flex;
  align-items: center;
  justify-content: center;

  .card {
    background-color: colors.$background-ui;
    border-radius: 18px;
    padding: 40px;
    display: flex;
    gap: 40px;
    align-items: flex-start;
  }

  .main {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 330px;
    flex-shrink: 0;

    .logo {
      --logo-inner: #{colors.$background-ui};
    }

    .title {
      @include typography.heading;
    }

    .description {
      @include typography.medium("semi-bold");

      color: colors.$text-subtle;
      line-height: 24px;
    }
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 600px;
    flex-shrink: 0;

    .details-label {
      @include typography.medium("bold");
    }

    .details-content {
      @include typography.medium;

      position: relative;
      background-color: colors.$background-grey-light;
      border-radius: 8px;
      padding: 20px;
      color: colors.$text;
      white-space: pre-wrap;
      overflow-y: auto;
      min-height: 300px;
      max-height: min(900px, 80vh);

      > .copy-button {
        position: absolute;
        right: 8px;
        top: 8px;
        opacity: 0;
      }

      &:hover > .copy-button,
      &:focus-within > .copy-button {
        opacity: 1;
      }
    }
  }
}
</style>
